import { AccountingTypeLabelModifier } from '@/features/master-table/models';
import { AdministrativeReformTeamEvaluationOptionLabelModifier } from '@/features/project/_models/project-filter';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { CandidateOfInspectionByExternalExpertLabelModifier } from '@/features/search/_models/projects-search';
import {
  getAmountSearchBaseBudgetTypeAndAmountData,
  getAmountSearchTagLabel,
  getSearchKeyword,
} from '@/features/search/_utils/projects-search';
import { t } from '@/i18n';
import { ITEM_SPLITTER_COMMA, logger } from '@/utils';
import {
  FundSheetTypeBefore2025LabelModifier,
  FundSheetTypeLabelModifier,
  ImplementationMethodLabelModifier,
  ProjectCategoryLabelModifier,
  ReviewTypeLabelModifier,
  SheetTypeLabelModifier,
} from '@/utils/enum';
import type { ProjectsPageSearchQueryStates } from '../../_hooks/search-projects/search-query-states';
import { convertFormulaConditionsToAmountSearchConditions } from '../../_utils/projects/convert-request-params';
import { PublishedLabelModifier } from '../_models/project-filter';

type ReturnType = { label: string; value: string | number };

type SearchQueryStateKeys = keyof ProjectsPageSearchQueryStates;
type ValueType<K extends SearchQueryStateKeys> =
  ProjectsPageSearchQueryStates[K];

const getNonNullableValue = <K extends SearchQueryStateKeys>(
  value: any,
): NonNullable<ValueType<K>> => {
  return value as NonNullable<ValueType<K>>;
};

const FILTER_ORDER = [
  'fiscalYear',
  'sheetType',
  'fundSheetType',
  'q',
  'organizationIds',
  'corporateNumber',
  'categories',
  'implementationMethods',
  'policyIds',
  'accountingType',
  'accountingTypeIds',
  'skipAdministrativeReformTeamEvaluation',
  'isPublished',
  'startYear',
  'endYear',
  'otherResponsibleOrganizationIds',
  'expenditureBudgetPurposeIds',
  'lastImplementedFiscalYearRangeMin',
  'lastImplementedFiscalYearRangeMax',
  'inspectionByExternalExpertReviewType',
  'isCandidateOfInspectionByExternalExpert',
  'formula_conditions',
  'amountSearchType',
  'projectNumbers',
] satisfies SearchQueryStateKeys[];

export const generateSearchHistoryLabelsMap = (
  filterCondition: ProjectsPageSearchQueryStates,
  inputCascaderAndAccountingLabels: {
    nestedOrganizationIds: string[];
    nestedOtherResponsibleOrganizationIds: string[];
    nestedPolicyIds: string[];
    nestedExpenditureBudgetPurposeIds: string[];
    accountingTypeIds: string[];
  },
  corporateName: string | null,
): ReturnType[] => {
  return FILTER_ORDER.flatMap<ReturnType>((key) => {
    const _value = filterCondition[key as SearchQueryStateKeys];
    const rsOnly = filterCondition.sheetType === 'RS';

    const cascaderKeys = [
      'organizationIds',
      'otherResponsibleOrganizationIds',
      'policyIds',
      'expenditureBudgetPurposeIds',
    ] as SearchQueryStateKeys[];

    if (
      (!_value && !cascaderKeys.includes(key)) ||
      (!rsOnly &&
        (key === 'policyIds' || key === 'expenditureBudgetPurposeIds'))
    ) {
      return [];
    }

    switch (key) {
      case 'fiscalYear': {
        const value = getNonNullableValue<typeof key>(_value);
        return [{ label: t('created-fiscal-year'), value }];
      }
      case 'sheetType': {
        const value = getNonNullableValue<typeof key>(_value);
        return [
          {
            label: t('sheet-type'),
            value: SheetTypeLabelModifier[value],
          },
        ];
      }
      case 'isPublished': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('publish-ability'),
          value: PublishedLabelModifier[value],
        };
      }
      case 'categories': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('project-category'),
          value: value
            .map((item) => ProjectCategoryLabelModifier[item])
            .join(ITEM_SPLITTER_COMMA),
        };
      }
      case 'fundSheetType': {
        const value = getNonNullableValue<typeof key>(_value);
        const fiscalYear = filterCondition.fiscalYear;
        const isSetBefore2025 = fiscalYear
          ? isBefore2025({ fiscalYear: Number(fiscalYear) })
          : false;

        return {
          label: t('fund-sheet-type'),
          value: isSetBefore2025
            ? FundSheetTypeBefore2025LabelModifier[value]
            : FundSheetTypeLabelModifier[value],
        };
      }
      case 'startYear': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('start-fiscal-year'),
          value,
        };
      }
      case 'endYear': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('end-fiscal-year'),
          value,
        };
      }
      case 'accountingType': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('accounting-type'),
          value: AccountingTypeLabelModifier[value],
        };
      }
      case 'accountingTypeIds': {
        return {
          label: t('accounting-name'),
          value:
            inputCascaderAndAccountingLabels.accountingTypeIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'implementationMethods': {
        const implementationMethods = getNonNullableValue<typeof key>(_value);
        return {
          label: t('implementation-method'),
          value: implementationMethods
            .map((item) => ImplementationMethodLabelModifier[item])
            .join(ITEM_SPLITTER_COMMA),
        };
      }
      case 'skipAdministrativeReformTeamEvaluation': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('filter-by-skip-administrative-reform-team-evaluation'),
          value: AdministrativeReformTeamEvaluationOptionLabelModifier[value],
        };
      }
      case 'projectNumbers': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('project-number'),
          value,
        };
      }
      case 'inspectionByExternalExpertReviewType': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('inspection-target'),
          value: value
            .map((item) => ReviewTypeLabelModifier[item])
            .join(ITEM_SPLITTER_COMMA),
        };
      }
      case 'isCandidateOfInspectionByExternalExpert': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('candidate-of-inspection-by-external-expert'),
          value: CandidateOfInspectionByExternalExpertLabelModifier[value],
        };
      }
      case 'corporateNumber': {
        if (!corporateName) {
          return [];
        }
        return {
          label: t('payment-name'),
          value: corporateName,
        };
      }
      case 'q': {
        const q = getNonNullableValue<typeof key>(_value);
        return {
          label: t('search-keyword'),
          value:
            getSearchKeyword(q, filterCondition.isSearchTargetProjectName) ?? q,
        };
      }
      case 'expenditureBudgetPurposeIds': {
        return {
          label: t('expenditure-budget-purpose'),
          value:
            inputCascaderAndAccountingLabels.nestedExpenditureBudgetPurposeIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'policyIds': {
        return {
          label: t('policy'),
          value:
            inputCascaderAndAccountingLabels.nestedPolicyIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'organizationIds': {
        return {
          label: t('organization-in-charge'),
          value:
            inputCascaderAndAccountingLabels.nestedOrganizationIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'otherResponsibleOrganizationIds': {
        return {
          label: t('other-organization-in-charge'),
          value:
            inputCascaderAndAccountingLabels.nestedOtherResponsibleOrganizationIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'formula_conditions': {
        const value = getNonNullableValue<typeof key>(_value);
        try {
          const conditions =
            convertFormulaConditionsToAmountSearchConditions(value);
          if (conditions && conditions.length > 0) {
            // 各条件を文字列に変換
            const conditionLabels = conditions
              .map((condition) => {
                const label = getAmountSearchTagLabel({
                  ...condition,
                  amountSearchBaseYearWithBudgetTypeEnd:
                    condition.amountSearchBaseYearWithBudgetTypeEnd ?? null,
                });
                return label || '';
              })
              .filter(Boolean);

            // 条件が取得できない場合は件数表示にフォールバック
            if (conditionLabels.length === 0) {
              return {
                label: t('formula-conditions'),
                value: t('formula-conditions-count', {
                  value: conditions.length,
                }),
              };
            }

            return {
              label: t('formula-conditions'),
              value: conditionLabels.join(', '),
            };
          }
        } catch (error) {
          logger.error(
            'Failed to parse formula_conditions for history label',
            error,
          );
        }
        return [];
      }
      case 'amountSearchType': {
        // 旧形式のURLパラメータからの変換処理（後方互換性のため保持）
        const {
          amountSearchType,
          amountSearchDiffAmount,
          amountSearchDiffAmountUnit,
          amountSearchDiffOperator,
          amountSearchRightTargetYear,
          amountSearchRightBudgetType,
          amountSearchRightAmountType,
          amountSearchBaseAmountType,
          amountSearchBaseBudgetType,
          amountSearchBaseTargetYear,
        } = filterCondition;

        // formula_conditionsが存在する場合は古い形式を無視
        if (filterCondition.formula_conditions) {
          return [];
        }

        // 古い個別フィールドが存在する場合のみ処理
        if (!amountSearchType) {
          return [];
        }

        const value = getAmountSearchTagLabel({
          amountSearchType,
          amountSearchBaseYearWithBudgetType:
            getAmountSearchBaseBudgetTypeAndAmountData({
              amountTargetFiscalYearParam: amountSearchBaseTargetYear,
              budgetType: amountSearchBaseBudgetType,
              budgetAmount: amountSearchBaseAmountType,
            }),
          amountSearchBaseYearWithBudgetTypeEnd:
            getAmountSearchBaseBudgetTypeAndAmountData({
              amountTargetFiscalYearParam: amountSearchRightTargetYear,
              budgetType: amountSearchRightBudgetType,
              budgetAmount: amountSearchRightAmountType,
            }),
          amountSearchDiffAmount,
          amountSearchDiffAmountUnit,
          amountSearchDiffOperator,
        });
        if (!value) {
          return [];
        }
        return {
          label: t('analysis-trend-search'),
          value,
        };
      }
      case 'lastImplementedFiscalYearRangeMin': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('last-implemented-fiscal-year-range-min'),
          value,
        };
      }
      case 'lastImplementedFiscalYearRangeMax': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('last-implemented-fiscal-year-range-max'),
          value,
        };
      }
      default: {
        const check: never = key;
        logger.error('key is not found', check);
        return [];
      }
    }
  });
};
